import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Play } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useCraftsCart } from "@/context/CraftsCartContext";
import YouMightLike from "@/components/crafts/YouMightLike";
import BundleBanner from "@/components/crafts/BundleBanner";

const timeAgo = (iso) => {
  if (!iso) return "";
  const secs = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  const units = [["day", 86400], ["hour", 3600], ["minute", 60]];
  for (const [label, s] of units) {
    const v = Math.floor(secs / s);
    if (v >= 1) return `${v} ${label}${v > 1 ? "s" : ""} ago`;
  }
  return "just now";
};

const MODULES = [
  { n: "one", level: "Start here", title: "The Braid Charm Set", meta: "The foundation technique", desc: "Turn shells, beads and metal findings into a clean, secure, removable adornment — a coordinated set of 6–10 braid charms, not a pile of unrelated pieces." },
  { n: "two", level: "Intermediate", title: "The Middle-Part Hair Jewel", meta: "2–3 hrs · ~£14–£25 in materials", desc: "One continuous head adornment that follows the middle parting, anchors discreetly into the style, and finishes with a controlled focal detail at the hairline." },
  { n: "three", level: "Intermediate", title: "The Cowrie Drop Earring", meta: "1–2 hrs · ~£12–£22 in materials", desc: "Jewellery that hangs from the ear — a matching pair that moves cleanly, stays light to wear, and finishes smoothly from every angle." },
  { n: "four", level: "Intermediate", title: "The Beaded Crown / Headband", meta: "3–5 hrs · ~£18–£35 in materials", desc: "A structured wearable headpiece that feels intentional from the front, secure at the sides and clean behind the ears." },
  { n: "five", level: "The finale", title: "The Full Adornment Set", meta: "Bring it all together", desc: "Plan one coordinated look using the braid charms, middle-part jewel, earrings and headpiece — considered, not identical." },
];

const AdornmentCode = ({ craft }) => {
  const { format } = useCurrency();
  const { add } = useCraftsCart();
  const imgs = [craft.cover_image, ...(craft.gallery || [])].filter(Boolean);
  const img = (i) => (imgs.length ? mediaUrl(imgs[i % imgs.length]) : null);
  const tiers = craft.tiers || [];
  const [proof, setProof] = useState(null);

  useEffect(() => { document.title = "The Adornment Code · Crafts"; }, []);
  useEffect(() => {
    if (!craft.slug) return;
    api.get(`/crafts/${craft.slug}/social-proof`).then((r) => setProof(r.data)).catch(() => {});
  }, [craft.slug]);
  const scrollToTiers = () => document.getElementById("tiers")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div data-testid="adornment-page" className="text-cream">
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}>
          <p className="text-[0.62rem] tracking-[0.4em] uppercase text-gold mb-6">A design guide from NoirbyAmaraOri</p>
          <h1 className="font-serif leading-[0.95]">
            <span className="block text-5xl sm:text-6xl lg:text-7xl">{craft.title || "The Adornment Code"}</span>
            <span className="block text-xl sm:text-2xl text-cream/70 mt-3 tracking-[0.2em] uppercase">{craft.subtitle || "Hair · Head · Ear"}</span>
          </h1>
          <p className="mt-8 font-serif text-2xl text-cream/90 leading-snug">Turn shells, beads and findings into adornments you can actually wear.</p>
          <p className="mt-4 text-cream/70 leading-relaxed max-w-lg">
            Five projects, built in order — each one adds a skill you carry into the next, from a single braid charm to a full coordinated set.
          </p>
          {(craft.stat_views || craft.stat_saves) && (
            <div className="mt-6 flex items-center gap-5 text-cream/60" data-testid="adornment-stats">
              {craft.stat_views && <div><span className="font-serif text-2xl text-gold">{craft.stat_views}</span><span className="ml-2 text-[0.6rem] tracking-[0.16em] uppercase">views</span></div>}
              {craft.stat_saves && <><div className="w-px h-6 bg-cream/15" /><div><span className="font-serif text-2xl text-gold">{craft.stat_saves}</span><span className="ml-2 text-[0.6rem] tracking-[0.16em] uppercase">saves</span></div></>}
              <span className="text-[0.6rem] tracking-[0.14em] uppercase text-cream/40">on the piece that inspired it</span>
            </div>
          )}
          {proof && proof.sold_total > 0 && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
              data-testid="adornment-social-proof"
              className="mt-6 inline-flex items-center gap-2.5 border border-gold/25 bg-cocoa/40 rounded-full pl-3 pr-4 py-1.5">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-gold/60 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-gold" />
              </span>
              <span className="text-[0.66rem] tracking-[0.14em] uppercase text-cream/70">
                {proof.sold_total} sold
                {proof.last_purchase_at && <span className="text-cream/40"> · latest {timeAgo(proof.last_purchase_at)}</span>}
              </span>
            </motion.div>
          )}
          {tiers[0] && (
            <button onClick={scrollToTiers} data-testid="adornment-hero-cta"
              className="mt-8 inline-flex items-center gap-2 bg-gold text-cocoa px-8 py-3.5 text-[0.7rem] tracking-[0.2em] uppercase hover:bg-cream transition-colors">
              Start the guide — {format(tiers[0].price)} <ArrowRight size={15} />
            </button>
          )}
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2 }}
          className="aspect-[4/5] bg-espresso/40 overflow-hidden">
          {img(0) ? <img src={img(0)} alt="The Adornment Code" className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center text-cream/20">Add cover image in Admin</div>}
        </motion.div>
      </section>

      {craft.video && (
        <section id="watch" data-testid="adornment-video" className="max-w-6xl mx-auto px-6 pt-4 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div className="[direction:ltr]">
            <p className="text-[0.62rem] tracking-[0.4em] uppercase text-gold mb-4">Watch it being made</p>
            <h2 className="font-serif text-3xl sm:text-4xl leading-tight">Real hands. Real shells.<br />No shortcuts.</h2>
            <p className="mt-5 text-cream/75 leading-relaxed max-w-md">
              Every piece in this guide is made by hand — from threading the first cowrie to styling the finished set. This is the exact craft you'll learn to do yourself.
            </p>
          </div>
          <div className="flex justify-center">
            <div className="relative w-full max-w-[340px] aspect-[9/16] bg-espresso/40 overflow-hidden border border-gold/15">
              <video src={mediaUrl(craft.video)} poster={img(0)} controls playsInline preload="metadata"
                data-testid="adornment-video-player" className="w-full h-full object-cover" />
            </div>
          </div>
        </section>
      )}

      {craft.card_video && (
        <section data-testid="adornment-card-video" className="max-w-6xl mx-auto px-6 pt-0 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div className="flex justify-center order-2 lg:order-1">
            <div className="relative w-full max-w-[340px] aspect-[9/16] bg-espresso/40 overflow-hidden border border-gold/15">
              <video src={mediaUrl(craft.card_video)} poster={img(0)} controls playsInline preload="metadata"
                data-testid="adornment-card-video-player" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="[direction:ltr] order-1 lg:order-2">
            <p className="text-[0.62rem] tracking-[0.4em] uppercase text-gold mb-4">{craft.card_video_eyebrow || "See what's inside"}</p>
            <h2 className="font-serif text-3xl sm:text-4xl leading-tight">{craft.card_video_heading || "The whole blueprint at a glance"}</h2>
            <p className="mt-5 text-cream/75 leading-relaxed max-w-md">
              A quick look at every module and tier — exactly what you get, from the first cowrie to the full styled set.
            </p>
          </div>
        </section>
      )}

      <div className="max-w-6xl mx-auto px-6 pb-6"><BundleBanner /></div>

      <section className="border-y border-gold/15 bg-cocoa/40">
        <div className="max-w-2xl mx-auto px-6 py-16 text-center space-y-5 text-cream/75 leading-relaxed">
          <p className="font-serif text-xl text-cream">This guide teaches how to turn shells, beads and metal findings into clean, secure, removable adornments.</p>
          <p>Each module builds on the last — the jewellery-finishing technique you learn first carries all the way through to the full set.</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-20">
        <p className="text-[0.62rem] tracking-[0.4em] uppercase text-gold mb-2 text-center">What's inside</p>
        <h2 className="font-serif text-3xl sm:text-4xl text-center mb-14">Five projects, in the order you'll build them</h2>
        <div className="space-y-16">
          {MODULES.map((m, i) => (
            <motion.div key={m.n} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}
              className={`grid lg:grid-cols-2 gap-8 lg:gap-14 items-center ${i % 2 ? "lg:[direction:rtl]" : ""}`}>
              <div className="aspect-[4/5] bg-espresso/40 overflow-hidden [direction:ltr]">
                {img(i + 1) ? <img src={img(i + 1)} alt={m.title} className="w-full h-full object-cover" /> : null}
              </div>
              <div className="[direction:ltr]">
                <p className="text-[0.6rem] tracking-[0.3em] uppercase text-gold">Module {m.n} · {m.level}</p>
                <h3 className="font-serif text-3xl sm:text-4xl mt-3">{m.title}</h3>
                <p className="text-[0.66rem] tracking-[0.14em] uppercase text-cream/40 mt-2">{m.meta}</p>
                <p className="text-cream/75 leading-relaxed mt-4">{m.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="tiers" className="border-t border-gold/15 bg-cocoa/40">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <p className="text-[0.62rem] tracking-[0.4em] uppercase text-gold mb-2 text-center">Choose your bundle</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-center mb-14">Start small, or take the whole blueprint</h2>
          <div className="grid md:grid-cols-3 gap-6 items-start">
            {tiers.map((t) => (
              <div key={t.id} data-testid={`adornment-tier-${t.id}`}
                className={`relative flex flex-col p-7 border ${t.badge ? "border-gold bg-cocoa/60" : "border-cream/15"}`}>
                {t.badge && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-cocoa text-[0.58rem] tracking-[0.2em] uppercase px-3 py-1">{t.badge}</span>}
                <h3 className="font-serif text-2xl">{t.name}</h3>
                <p className="font-serif text-4xl text-gold mt-3 tabular-nums">{format(t.price)}</p>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {(t.includes || []).map((inc, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-cream/80">
                      <Check size={15} className="text-gold shrink-0 mt-0.5" /> {inc}
                    </li>
                  ))}
                </ul>
                {t.sold_out ? (
                  <div data-testid={`adornment-soldout-${t.id}`}
                    className="mt-7 w-full py-3.5 text-center text-[0.68rem] tracking-[0.2em] uppercase border border-cream/15 text-cream/40 cursor-not-allowed">
                    Sold out
                  </div>
                ) : (
                  <button onClick={() => add(craft, t)} data-testid={`adornment-buy-${t.id}`}
                    className={`mt-7 w-full py-3.5 text-[0.68rem] tracking-[0.2em] uppercase transition-colors ${
                      t.badge ? "bg-gold text-cocoa hover:bg-cream" : "border border-cream/25 text-cream hover:border-gold hover:text-gold"}`}>
                    {t.cta || "Get it"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">Made by hand, then shared.<br />Now it's yours to make.</h2>
        {tiers[0] && (
          <button onClick={scrollToTiers} data-testid="adornment-closing-cta"
            className="mt-10 inline-flex items-center gap-2 bg-gold text-cocoa px-8 py-3.5 text-[0.7rem] tracking-[0.2em] uppercase hover:bg-cream transition-colors">
            Start The Adornment Code — from {format(tiers[0].price)}
          </button>
        )}
        <p className="mt-10 text-[0.62rem] tracking-[0.24em] uppercase text-cream/40">A NoirbyAmaraOri Crafts guide</p>
      </section>
      <YouMightLike currentSlug={craft.slug} />
    </div>
  );
};

export default AdornmentCode;
